"use client";
import { Check, Monitor, Moon, SunDim, ChevronDown, LogOut, PanelRight } from "lucide-react";

import NotificationModal from "@/components/tailwind/ui/modals/notificationModal"
import EditorOptionsMenu from "@/components/tailwind/ui/modals/editorOptionsMenu"
import VisitorBadges from "@/components/tailwind/presence/VisitorBadges"
import LastEditedLabel from "@/components/tailwind/presence/LastEditedLabel"

import { useTheme } from "next-themes";
import { useAuth } from "@/hooks/use-auth";
import { useNoteContext } from "@/contexts/NoteContext";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { extractNoteId } from "@/utils/noteUrl";
// import { useRootPagesOrder } from "@/contexts/rootPagesOrderContext";
// import { useWorkspaceContext } from "@/contexts/workspaceContext";
// import { useBlockActions } from "@/contexts/blockContext";
// import { addFavouritePageMutation, removeFavouritePageMutation } from "@/indexedDB";
// import { useLocalSidebarOrder } from "@/indexedDB/hooks/useLocalSidebarOrder";
// import { toast } from "@/lib/toast";

export const appearances = [
  {
    theme: "System",
    icon: <Monitor className="h-4 w-4" />,
  },
  {
    theme: "Light",
    icon: <SunDim className="h-4 w-4" />,
  },
  {
    theme: "Dark",
    icon: <Moon className="h-4 w-4" />,
  },
];

export default function Menu() {
  const { theme: currentTheme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Hidden-properties side panel toggle. State lives in NoteContext and is
  // published by the editor's EditorPropertiesSection; the button shows only
  // when the current full-view page actually has hidden properties.
  const { hasHiddenDetails, detailsPanelOpen, setDetailsPanelOpen } = useNoteContext();
  // const { favouritePagesOrder: favouriteFromContext, addFavouritePage, removeFavouritePage } = useRootPagesOrder();
  // const { favouritePagesOrder: favouriteFromIDB } = useLocalSidebarOrder();
  // const { currentWorkspace } = useWorkspaceContext();
  // const { getBlock } = useBlockActions();

  const isNotePage = pathname?.includes("/notes/");
  // A board view carries the `view` query param; pages never do.
  const isViewCollection = searchParams?.has("view");
  // The foreground document id comes from the route. The header sits beside
  // the page content (not above it), so the route is the one shared place that
  // always knows which note/board is open. Passing it as a prop makes the
  // kebab independent of the editor's eventBus broadcast (which boards never
  // emit) — fixing the "short menu after opening a board" bug.
  const routeNoteId = extractNoteId(pathname?.split("/").pop());
  const noteIdForMenu = routeNoteId && routeNoteId !== "notes" ? routeNoteId : undefined;
  // const favouritePagesOrder = favouriteFromIDB.length > 0 ? favouriteFromIDB : favouriteFromContext;
  // const noteId = pathname?.split("/").pop() ?? null;
  // const datasourceId = searchParams?.get("v");
  // const targetFavouriteId = datasourceId || noteId;
  // const targetBlock = targetFavouriteId ? getBlock(targetFavouriteId) : null;
  // const isFavourited = !!targetFavouriteId && favouritePagesOrder.includes(targetFavouriteId);
  // const canFavourite =
  //   isFavourited ||
  //   (!!targetBlock &&
  //     (targetBlock.blockType === "page" || targetBlock.blockType === "collection_view"));

  // const handleToggleFavourite = async () => {
  //   if (!targetFavouriteId || !currentWorkspace?._id || !canFavourite) return;
  //   try {
  //     if (isFavourited) {
  //       removeFavouritePage(targetFavouriteId);
  //       await removeFavouritePageMutation(targetFavouriteId, currentWorkspace._id);
  //       toast.success("Removed from favourites");
  //     } else {
  //       addFavouritePage(targetFavouriteId);
  //       await addFavouritePageMutation(targetFavouriteId, currentWorkspace._id);
  //       toast.success("Added to favourites");
  //     }
  //   } catch (error) {
  //     console.error("Failed to update favourites", error);
  //     toast.error("Failed to update favourites");
  //   }
  // };

  if (!user) return null;

  return (
<div className="flex items-center gap-3">
      {/* 🕒 When this page was last edited, and by whom */}
      {isNotePage && !isViewCollection && <LastEditedLabel noteId={noteIdForMenu} />}
      {/* 👥 Who's here now / was here recently */}
      {isNotePage && !isViewCollection && <VisitorBadges blockId={noteIdForMenu} />}
      {/* ⚙️ More Options Modal for Notes */}
      {isNotePage && !isViewCollection && <EditorOptionsMenu noteId={noteIdForMenu} />}
      {/* {isNotePage && canFavourite && (
        <button
          type="button"
          onClick={handleToggleFavourite}
          disabled={!targetFavouriteId}
          title={isFavourited ? "Remove from favourites" : "Add to your favourites"}
          aria-label={isFavourited ? "Remove from favourites" : "Add to your favourites"}
          className="flex items-center gap-2 hover-component p-1 rounded-md transition  disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Star
            className={`w-5 h-5 transition-colors ${
              isFavourited ? "text-[#f6c050] fill-[#f6c050]" : "text-gray-500 dark:text-gray-300"
            }`}
          />
        </button>
      )} */}
      {/* Hidden-properties panel toggle — mirrors the peek's header button, but
          for the full-view editor. Only shown when the open page is a database
          row with hidden properties (flag published via NoteContext). */}
      {isNotePage && !isViewCollection && hasHiddenDetails && (
        <button
          type="button"
          title={detailsPanelOpen ? "Hide properties panel" : "Show properties panel"}
          aria-label={detailsPanelOpen ? "Hide properties panel" : "Show properties panel"}
          onClick={() => setDetailsPanelOpen((v) => !v)}
          className={`flex items-center p-1 rounded-md text-gray-500 dark:text-gray-300 transition-colors hover-component  ${
            detailsPanelOpen ? "bg-selected" : ""
          }`}
        >
          <PanelRight className="w-[18px] h-[18px]" />
        </button>
      )}

       {/* 🔔 Notification Modal */}
      <div className="relative">
        <NotificationModal/>
      </div>
      {/* <Popover>
        <PopoverTrigger asChild>
          <button title="Account and settings" className="flex items-center gap-2 hover:opacity-80 transition group">
            <div className="relative h-[30px] w-[30px] overflow-hidden rounded-full border-2 border-transparent group-hover:border-gray-300 dark:group-hover:border-component transition-colors">
              {user.image && (
                <Image
                src={user.image}
                alt="Profile"
                fill
                className="object-cover"
                />
              )}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-64 p-2 rounded-lg shadow-lg border border-component bg-[#f8f8f7] dark:bg-[hsl(0deg_0%_12.55%)]"
          align="end"
          sideOffset={8}
          > */}
          {/* User Profile Section */}
          {/* <div className="flex items-center gap-3 p-3 border-b border-component">
            <div className="relative h-10 w-10 overflow-hidden rounded-full">
              {user.image && (
                <Image
                src={user.image}
                alt="Profile"
                fill
                className="object-cover"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {user.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user.email}
              </p>
            </div>
          </div> */}

          {/* Appearance Section */}
          {/* <div className="p-2">
            <p className="px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              APPEARANCE
            </p>
            <div className="space-y-1">
              {appearances.map(({ theme, icon }) => (
                <button
                key={theme}
                className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${currentTheme === theme.toLowerCase()
                  ? "bg-selected text-gray-900 dark:text-gray-100"
                  : "hover-component text-gray-700 dark:text-gray-300"
                  }`}
                  onClick={() => setTheme(theme.toLowerCase())}
                  >
                  <div className="flex items-center gap-3">
                    <div className="text-gray-500 dark:text-gray-400">
                      {icon}
                    </div>
                    <span>{theme}</span>
                  </div>
                  {currentTheme === theme.toLowerCase() && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          </div> */}

          {/* Sign Out Button */}
          {/* <div className="p-2 pt-1 border-t border-component">
            <button
              onClick={logout}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              >
              <LogOut className="h-4 w-4" />
              <span>Sign out</span>
            </button>
          </div>
        </PopoverContent>
      </Popover> */}
    </div>

  );
}